import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

const ACK_STORAGE_KEY = "thema:early-access:acknowledged";

const OAK_AUDIT_ANNOUNCEMENT = "https://x.com/SecurityOak/status/2006026823440716105";
const OAK_AUDIT_REPORT_PDF =
  "https://github.com/oak-security/audit-reports/blob/main/Thema/2025-12-27%20Audit%20Report%20-%20Thema%20Protocol.pdf";

// TODO(ops): confirm the live redemption window once ops signs off.
// Beta target is "as fast as possible"; will lengthen post-audit to keep
// capital deployed.
const REDEMPTION_WINDOW = "approximately 24 hours";

// Live results - TBTC01 on Base. Window covers 2026-03-05 → 2026-05-06.
// Numbers come from Thema Labs' validation capital deployment; not a projection.
const LIVE_RESULTS = {
  navAccretionPct: 7.86,
  navDays: 63,
  navStartRatio: "1.0000",
  navEndRatio: "1.0786",
  lpTwrPct: 4.47,
  lpDays: 60,
  lpAnnualizedPct: 27, // matches what the user wrote
  btcMovePct: 22,
  peakTvlUsd: 40_500,
  // Volume efficiency: $8.6M volume on ~$60k TVL over 60 days →
  // $71.7M monthly volume per $1M TVL.
  volPerMTvl: "$71.7M",
  volDays: 30,
  windowLabel: "Mar 5 – May 6, 2026",
} as const;

const navAnnualized = Math.round(
  (LIVE_RESULTS.navAccretionPct * 365) / LIVE_RESULTS.navDays,
);

export default function Disclosure() {
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    try {
      setAcknowledged(sessionStorage.getItem(ACK_STORAGE_KEY) === "1");
    } catch {
      /* sessionStorage unavailable */
    }
  }, []);

  const onToggle = (next: boolean) => {
    setAcknowledged(next);
    try {
      if (next) sessionStorage.setItem(ACK_STORAGE_KEY, "1");
      else sessionStorage.removeItem(ACK_STORAGE_KEY);
    } catch {
      /* sessionStorage unavailable */
    }
  };

  const onContinue = () => {
    if (!acknowledged) return;
    navigate("/early-access/consent");
  };

  return (
    <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
      <article className="mx-auto max-w-[760px] space-y-12">
        {/* ─── Header ─────────────────────────────────────────────────── */}
        <header className="space-y-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            EARLY ACCESS &middot; PRIVATE BETA
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold leading-tight">
            ThemaBTC on Base - cbBTC MAST
          </h1>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/80 max-w-[60ch]">
            Starting backing ratio: <span className="text-white">1 ThemaBTC = 1 cbBTC</span>.
            Ratio grows automatically as arbitrage surplus compounds into NAV.
          </p>
          <div className="h-px w-16 bg-white/15" />
        </header>

        {/* ─── What is ThemaBTC ───────────────────────────────────────── */}
        <section className="space-y-4">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
            What is ThemaBTC
          </h2>
          <p className="text-[15px] leading-relaxed text-thema-text-soft/90">
            Thema builds <span className="text-white">Market-Aware Structured Tokens (MASTs)</span>:
            on-chain assets with arbitrage capture and automated yield compounding
            built directly into the instrument. The same value flow that Authorized
            Participants like Goldman and Jane Street have extracted via ETF primary
            markets for fifty years - the spread between a structured product and
            its NAV - is now permissionless, non-custodial, and compounds into every
            holder&rsquo;s backing ratio automatically.
          </p>
          <p className="text-[15px] leading-relaxed text-thema-text-soft/90">
            ThemaBTC is a cbBTC-backed MAST on Base. Provide liquidity to the vault
            to earn price-independent yield{" "}
            on BTC. Yield numbers are based on live deployemt over the past 2 months, from thema labs internal captial of <span className="text-white">~$40k</span>:
          </p>
        </section>

        {/* ─── Live results ───────────────────────────────────────────── */}
        <section className="space-y-4">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
              Live results - TBTC01 on Base
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-micro">
              {LIVE_RESULTS.windowLabel}
            </span>
          </div>

          <div className="space-y-3">
            <ResultCard
              featured
              label="LP APY · vault depositors"
              primary={`+${LIVE_RESULTS.lpAnnualizedPct}% annualized`}
              meta={`${LIVE_RESULTS.lpDays} days`}
              footer={`LP TWR +${LIVE_RESULTS.lpTwrPct.toFixed(2)}% · ${LIVE_RESULTS.lpDays} days`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ResultCard
                label="NAV accretion · passive holders"
                primary={`+${LIVE_RESULTS.navAccretionPct.toFixed(2)}%`}
                meta={`${LIVE_RESULTS.navDays} days · ~${navAnnualized}% annualized`}
                footer={``}
              />
              <ResultCard
                label="Vol generated per $1M TVL · last 30 days"
                primary={LIVE_RESULTS.volPerMTvl}
                meta=""
                footer={`$8.6M volume on ~$60k TVL in 60 days, equivalent to ${LIVE_RESULTS.volPerMTvl} monthly volume per $1M TVL.`}
              />
            </div>
          </div>
        </section>

        {/* ─── Who this is for ────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
            Who this is for
          </h2>
          <ul className="space-y-2 text-[14px] text-thema-text-soft/85 pl-5 list-disc marker:text-white/30">
            <li>
              BTC holders looking for delta-neutral yield on their BTC without
              giving up custody or taking directional risk.
            </li>
            <li>
              Foundations and treasuries holding idle BTC who want a compounding
              structured product with transparent on-chain mechanics.
            </li>
            <li>
              LPs and yield seekers who want to deploy USDC into a vault earning
              from BTC arbitrage spread.
            </li>
            <li>
              Arb operators who want permissionless access to mint/redeem against
              live price dislocations.
            </li>
          </ul>
        </section>

        {/* ─── Audited & live ─────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
            What&rsquo;s audited and live
          </h2>
          <ul className="space-y-2 text-[14px] text-thema-text-soft/85 pl-5 list-disc marker:text-white/30">
            <li>
              {/* copy-lint-allow: factual audit reference */}
              Core MAST contracts - audited by{" "}
              <a
                href={OAK_AUDIT_ANNOUNCEMENT}
                target="_blank"
                rel="noreferrer"
                className="text-thema-royal-blue hover:underline"
              >
                Oak Security
              </a>{" "}
              (
              <a
                href={OAK_AUDIT_REPORT_PDF}
                target="_blank"
                rel="noreferrer"
                className="text-thema-royal-blue hover:underline"
              >
                report PDF
              </a>
              ).
            </li>
            <li>
              {/* copy-lint-allow: factual audit reference */}
              <code>ArbitrageFeeModel</code> and the oracle stack - audited.
            </li>
            <li>
              Real-time vault and position monitoring via Hypernative -
              active.
            </li>
          </ul>
        </section>

        {/* ─── Not yet audited ────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
            What&rsquo;s not yet audited
          </h2>
          <ul className="space-y-2 text-[14px] text-thema-text-soft/85 pl-5 list-disc marker:text-white/30">
            <li>The vault contract (<code>ThemaLPVault</code>).</li>
            <li>The Gnosis Safe configuration.</li>
          </ul>
        </section>

        {/* ─── Redemption timing ──────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-thema-teal">
            Redemption timing
          </h2>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/85">
            Redemptions from the LP vault are <span className="text-white">asynchronous</span>.
            Withdrawals up to <span className="text-white">15% of TVL</span> are
            available <span className="text-white">instantly</span>. Any withdrawal
            larger than 15% of TVL becomes available within{" "}
            <span className="text-white">{REDEMPTION_WINDOW}</span>. We are
            keeping the redemption window short by design so deposit barriers
            stay low. Once audits complete the window will lengthen to keep more
            capital usefully deployed.
          </p>
        </section>

        {/* ─── What this means for you ────────────────────────────────── */}
        <aside className="rounded-md border border-amber-500/30 bg-amber-500/5 p-5 space-y-3">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-300/90">
            What this means for you
          </h3>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/85">
            This is a private beta. We have been running it with our own capital
            since January. The unaudited Vault and Safe components carry real
            risk. Do not deposit funds you are not prepared to lose entirely.
          </p>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/85">
            We have onboarded Hypernative and monitor threats and risks in real
            time, but unaudited is unaudited, and accordingly carries risk.
            Audit completion is targeted in the next release.
          </p>
        </aside>

        {/* ─── Acknowledgement ────────────────────────────────────────── */}
        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-4">
          <p className="text-[14px] leading-relaxed text-thema-text-soft/90">
            <em>
              I have read the above and understand that the Thema Vault and Safe
              contracts I am interacting with are not yet audited. I accept the
              risk of total loss of deposited funds. By signing the transaction
              on the next step, I am recording this acknowledgement on-chain.
            </em>
          </p>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => onToggle(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer accent-thema-royal-blue"
            />
            <span className="text-[13px] text-thema-text-soft">
              I have read and understood the above. I want to proceed to signing.
            </span>
          </label>
        </section>

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/"
            className="font-mono text-[11px] uppercase tracking-[0.15em] text-thema-micro hover:text-white/70"
          >
            ← Back
          </Link>
          <button
            type="button"
            onClick={onContinue}
            disabled={!acknowledged}
            className="px-5 py-2.5 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium transition hover:bg-thema-royal-blue/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-thema-royal-blue/15"
          >
            Proceed to Consent →
          </button>
        </div>
      </article>
    </main>
  );
}

function ResultCard({
  label,
  primary,
  meta,
  footer,
  featured = false,
}: {
  label: string;
  primary: string;
  meta: string;
  footer: string;
  featured?: boolean;
}) {
  if (featured) {
    return (
      <div className="relative overflow-hidden rounded-md border border-thema-royal-blue/40 bg-gradient-to-br from-thema-royal-blue/15 via-thema-royal-blue/5 to-thema-teal/10 p-6 flex flex-col gap-3 shadow-[0_0_24px_-8px_rgba(77,183,247,0.35)]">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-thema-teal">
          {label}
        </p>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-mono text-4xl md:text-5xl font-semibold text-white leading-none tracking-tight tabular-nums">
            {primary}
          </span>
          <span className="font-sans text-[13px] text-thema-text-soft/80">{meta}</span>
        </div>
        {footer && (
          <p className="font-sans text-[13px] leading-relaxed text-thema-text-soft/75">
            {footer}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-thema-border-hairline bg-white/[0.025] p-4 flex flex-col gap-2">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-2xl font-semibold text-white leading-none tracking-tight tabular-nums">
          {primary}
        </span>
        <span className="font-sans text-[11px] text-thema-text-soft/70">{meta}</span>
      </div>
      <p className="font-sans text-[12px] leading-relaxed text-thema-text-soft/65">
        {footer}
      </p>
    </div>
  );
}
